import { Button } from '@/components/button';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import { Modal } from '@/components/modal';
import { HSeparator } from '@/components/separator';
import Seat1Template from '@/routes/org/event/new/step-2/templates/_seats/seats-1.json';
import { api } from '@/services/api';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import {
  DocumentCheckIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { Edit2, Monitor, Plus, Save, Settings, Trash2, X } from 'lucide-react';
import moment from 'moment';
import { SpinnerIcon } from 'primereact/icons/spinner';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

interface Seat {
  id: string;
  row: string;
  label: string;
  type: string;
  state: string;
  seatGroupId?: string;
}

interface SeatGroup {
  id: string;
  name: string;
  price: number;
  color: string;
  seats: string[]; // Array of seat IDs
}

interface Showtime {
  id: string;
  time: string;
  seats: Seat[]; // Each showtime has its own seat layout
  seatGroups: SeatGroup[];
  bookedSeats: string[];
}

interface Screen {
  id: string;
  name: string;
  start_sell_at: string;
  end_sell_at: string;
  date: string;
  showtimes: Showtime[];
  seats: Seat[];
}

const SEAT_GROUP_COLORS = [
  '#3b82f6', // bg-blue-500
  '#10b981', // bg-green-500
  '#8b5cf6', // bg-purple-500
  '#ec4899', // bg-pink-500
  '#6366f1', // bg-indigo-500
  '#06b6d4', // bg-cyan-500
  '#f97316', // bg-orange-500
];

const defaultScreenForm = {
  state: false,
  label: 'create',
  name: '',
  date: '',
  start_sell_at: '',
  end_sell_at: '',
};

type SeatingBookingSystemProps = {
  event: any; // or better: specific type
};

const SeatingBookingSystem: React.FC<SeatingBookingSystemProps> = ({
  event,
}) => {
  const [screens, setScreens] = useState<Screen[]>([]);
  const [selectedScreen, setSelectedScreen] = useState<Screen | null>(null);
  const [showScreenForm, setShowScreenForm] = useState(defaultScreenForm);
  const [selectedShowtime, setSelectedShowtime] = useState<Showtime | null>(
    null
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
  const [editingSeatGroup, setEditingSeatGroup] = useState<SeatGroup | null>(
    null
  );
  const [newSeatGroupName, setNewSeatGroupName] = useState('');
  const [newSeatGroupPrice, setNewSeatGroupPrice] = useState(0);
  const [newShowtimeTime, setNewShowtimeTime] = useState('');
  const [newShowtimeDate, setNewShowtimeDate] = useState('');
  const [showDeleteScreenModal, setShowDeleteScreenModal] = useState(false);
  const [addSeatType, setAddSeatType] = useState<'seat' | 'space'>('seat');
  const [pendingUpdates, setPendingUpdates] = useState<
    Array<{ action: string; data: any }>
  >([]);

  const { errors } = useSelector((state: any) => state.event);

  const fetchScreen = () => {
    api
      .get(`/org/events/${event.code}/screens`)
      .then((response: any) => {
        const { data } = response.data;

        if (data) {
          setScreens(data);
          setSelectedScreen(data[0]);
        }
      })
      .catch((error: any) => {
        toast.error('Error in server, try again later');
      });
  };

  useEffect(() => {
    fetchScreen();
  }, []);

  // Screen management
  const createNewScreen = () => {
    const sampleSeats: Seat[] = Seat1Template;

    const sampleShowtimeSeats = JSON.parse(JSON.stringify(sampleSeats));

    const newScreen: Screen = {
      name: showScreenForm.name,
      date: showScreenForm.date,
      start_sell_at: showScreenForm.start_sell_at,
      end_sell_at: showScreenForm.end_sell_at,
      seats: sampleShowtimeSeats,
      id: Date.now().toString(),
      showtimes: [],
    };

    api
      .post(`/org/events/${event.id}/screens`, newScreen)
      .then((response: any) => {
        setScreens([...screens, { ...newScreen, id: response.data.data.id }]);
        setSelectedScreen({ ...newScreen, id: response.data.data.id });
        setShowScreenForm(defaultScreenForm);
      });
  };

  const updateScreen = (screenId: any) => {
    let newScreen = screens.map((screen) =>
      screen.id == screenId ? { ...screen, ...showScreenForm } : screen
    );

    api
      .patch(`/org/events/${event.id}/screens/${screenId}`, newScreen[0])
      .then((response: any) => {
        fetchScreen();

        setShowScreenForm({ ...showScreenForm, state: false });
        setScreens(newScreen);
      });
  };

  const deleteScreen = (screenId: string) => {
    setScreens(screens.filter((s) => s.id !== screenId));
    if (selectedScreen?.id === screenId) {
      setSelectedScreen(screens[0]);
    }
  };

  // Batch update function for efficiency
  const batchUpdateToBackend = () => {
    if (pendingUpdates.length === 0) return;

    let removedSeats: any = [];
    let addedSeats: any = [];

    pendingUpdates.map((update: any) => {
      if (update.action === 'remove_seat') {
        removedSeats.push(update.data.seatId);
      }

      if (update.action === 'add_seat') {
        addedSeats.push(update.data.seat);
      }
    });

    if (addedSeats.length > 0) {
      api
        .post(
          `/org/events/${event.id}/showtimes/${selectedShowtime?.id}/batch-seats`,
          {
            seats: addedSeats,
          }
        )
        .then((response: any) => {
          setSelectedShowtime((prev) => {
            if (!prev) return null;

            // Remove the temporarily added seats and add the database seats
            const seatsWithoutAdded = prev.seats.filter(
              (seat) =>
                !addedSeats.some((addedSeat: any) => addedSeat.id === seat.id)
            );

            return {
              ...prev,
              seats: [...seatsWithoutAdded, ...response.data],
            };
          });
        })
        .catch((error: any) => {
          toast.error('Error in server, please try again later');
        });
    }

    if (removedSeats.length > 0) {
      api
        .post(`/org/events/${event.id}/batch-seats-delete`, {
          ids: removedSeats,
        })
        .then((response: any) => {})
        .catch((error: any) => {
          toast.error('Error in server, please try again later');
        });
    }

    // Clear pending updates after sending
    setPendingUpdates([]);
  };

  const deleteStudio = (studioId: any) => {
    api
      .delete(`/org/events/${event.id}/screens/${studioId}`)
      .then((response: any) => {
        fetchScreen();
        setShowDeleteScreenModal(false);
      })
      .catch((error: any) => {
        toast.error('Error in server, please try again later');
      });
  };

  // Debounced batch update (sends updates every 2 seconds)
  useEffect(() => {
    if (pendingUpdates.length === 0) return;

    const timer = setTimeout(batchUpdateToBackend, 2000);
    return () => clearTimeout(timer);
  }, [pendingUpdates]);

  const addPendingUpdate = (action: string, data: any) => {
    setPendingUpdates((prev) => [
      ...prev,
      { action, data, timestamp: Date.now() },
    ]);
  };

  const createNewShowtime = () => {
    if (!selectedScreen || !newShowtimeTime) return;

    const newShowtime: Showtime = {
      id: `show-${Date.now()}`,
      time: newShowtimeTime,
      seats: JSON.parse(JSON.stringify(selectedScreen.seats)),
      seatGroups: [],
      bookedSeats: [],
    };

    api
      .post(`/org/events/${event.id}/screens/${selectedScreen.id}/showtimes`, {
        ...newShowtime,
        schedule_at: selectedScreen.date + ' ' + newShowtime.time,
      })
      .then((response: any) => {
        const updatedScreens = screens.map((screen) =>
          screen.id === selectedScreen.id
            ? {
                ...screen,
                showtimes: [
                  ...screen.showtimes,
                  {
                    ...newShowtime,
                    id: response.data.data.id,
                    seats: response.data.data.seats,
                  },
                ],
              }
            : screen
        );

        setScreens(updatedScreens);
        setSelectedScreen(
          updatedScreens.find((s) => s.id === selectedScreen.id)!
        );
        setSelectedShowtime({
          ...newShowtime,
          id: response.data.data.id,
          seats: response.data.data.seats,
        });
        setNewShowtimeTime('');
      });

    addPendingUpdate('create_showtime', newShowtime);
  };

  const addSeatToLayout = (
    row: string,
    position: number,
    type: 'seat' | 'space' = addSeatType
  ) => {
    if (!selectedScreen || !selectedShowtime) return;

    const newSeat: Seat = {
      id: `${type}-${row.toLowerCase()}${position}-${Date.now()}`,
      row,
      label: position.toString(),
      type,
      state: 'available',
    };

    const updatedScreens = screens.map((screen) =>
      screen.id === selectedScreen.id
        ? {
            ...screen,
            showtimes: screen.showtimes.map((showtime) =>
              showtime.id === selectedShowtime.id
                ? { ...showtime, seats: [...showtime.seats, newSeat] }
                : showtime
            ),
          }
        : screen
    );

    setScreens(updatedScreens);
    setSelectedScreen(updatedScreens.find((s) => s.id === selectedScreen.id)!);
    setSelectedShowtime((prev) =>
      prev
        ? {
            ...prev,
            seats: [...prev.seats, newSeat],
          }
        : null
    );

    addPendingUpdate('add_seat', {
      showtimeId: selectedShowtime.id,
      seat: newSeat,
    });
  };

  const removeSeatFromLayout = (seatId: string) => {
    if (!selectedScreen || !selectedShowtime) return;

    // Check if seat is booked
    const seat = selectedShowtime.seats.find((s) => s.id === seatId);
    if (seat?.state === 'booked') return;

    const updatedScreens = screens.map((screen) =>
      screen.id === selectedScreen.id
        ? {
            ...screen,
            showtimes: screen.showtimes.map((showtime) =>
              showtime.id === selectedShowtime.id
                ? {
                    ...showtime,
                    seats: showtime.seats.filter((seat) => seat.id !== seatId),
                    seatGroups: showtime.seatGroups.map((group) => ({
                      ...group,
                      seats: group.seats.filter((id) => id !== seatId),
                    })),
                  }
                : showtime
            ),
          }
        : screen
    );

    setScreens(updatedScreens);
    setSelectedScreen(updatedScreens.find((s) => s.id === selectedScreen.id)!);
    setSelectedShowtime((prev) =>
      prev
        ? {
            ...prev,
            seats: prev.seats.filter((seat) => seat.id !== seatId),
            seatGroups: prev.seatGroups.map((group) => ({
              ...group,
              seats: group.seats.filter((id) => id !== seatId),
            })),
          }
        : null
    );

    addPendingUpdate('remove_seat', {
      showtimeId: selectedShowtime.id,
      seatId,
    });
  };

  const updateSeatState = (
    seatId: string,
    newState: 'available' | 'disabled' | 'booked'
  ) => {
    if (!selectedScreen || !selectedShowtime) return;

    const updatedScreens = screens.map((screen) =>
      screen.id === selectedScreen.id
        ? {
            ...screen,
            showtimes: screen.showtimes.map((showtime) =>
              showtime.id === selectedShowtime.id
                ? {
                    ...showtime,
                    seats: showtime.seats.map((seat) =>
                      seat.id === seatId ? { ...seat, state: newState } : seat
                    ),
                  }
                : showtime
            ),
          }
        : screen
    );

    setScreens(updatedScreens);
    setSelectedScreen(updatedScreens.find((s) => s.id === selectedScreen.id)!);
    setSelectedShowtime((prev) =>
      prev
        ? {
            ...prev,
            seats: prev.seats.map((seat) =>
              seat.id === seatId ? { ...seat, state: newState } : seat
            ),
          }
        : null
    );

    addPendingUpdate('update_seat_state', {
      showtimeId: selectedShowtime.id,
      seatId,
      state: newState,
    });
  };

  const createSeatGroup = () => {
    if (
      !selectedScreen ||
      !selectedShowtime ||
      !newSeatGroupName ||
      selectedSeats.length === 0
    )
      return;

    const newSeatGroup: SeatGroup = {
      id: `group-${Date.now()}`,
      name: newSeatGroupName,
      price: newSeatGroupPrice,
      color:
        SEAT_GROUP_COLORS[
          selectedShowtime.seatGroups.length % SEAT_GROUP_COLORS.length
        ],
      seats: [...selectedSeats],
    };

    api
      .post(
        `/org/events/${event.id}/showtimes/${selectedShowtime.id}/seat-groups`,
        newSeatGroup
      )
      .then((response: any) => {
        const updatedScreens = screens.map((screen) =>
          screen.id === selectedScreen.id
            ? {
                ...screen,
                showtimes: screen.showtimes.map((showtime) =>
                  showtime.id === selectedShowtime.id
                    ? {
                        ...showtime,
                        seatGroups: [
                          ...showtime.seatGroups,
                          { ...response.data, seats: selectedSeats },
                        ],
                      }
                    : showtime
                ),
              }
            : screen
        );

        setScreens(updatedScreens);
        setSelectedScreen(
          updatedScreens.find((s) => s.id === selectedScreen.id)!
        );

        setSelectedShowtime({
          ...selectedShowtime,
          seatGroups: [
            ...selectedShowtime.seatGroups,
            { ...response.data, seats: selectedSeats },
          ],
        });
        setSelectedSeats([]);
        setNewSeatGroupName('');
        setNewSeatGroupPrice(0);
      })
      .catch((error: any) => {});
  };

  const updateSeatGroup = (groupId: string, updates: Partial<SeatGroup>) => {
    if (!selectedScreen || !selectedShowtime) return;

    const updatedScreens = screens.map((screen) =>
      screen.id === selectedScreen.id
        ? {
            ...screen,
            showtimes: screen.showtimes.map((showtime) =>
              showtime.id === selectedShowtime.id
                ? {
                    ...showtime,
                    seatGroups: showtime.seatGroups.map((group) =>
                      group.id === groupId ? { ...group, ...updates } : group
                    ),
                  }
                : showtime
            ),
          }
        : screen
    );

    api
      .patch(`/org/events/${event.id}/seat-groups/${groupId}`, updates)
      .then(() => {
        setScreens(updatedScreens);
        setSelectedScreen(
          updatedScreens.find((s) => s.id === selectedScreen.id)!
        );
        setSelectedShowtime((prev) =>
          prev
            ? {
                ...prev,
                seatGroups: prev.seatGroups.map((group) =>
                  group.id === groupId ? { ...group, ...updates } : group
                ),
              }
            : null
        );
      });
  };

  const deleteSeatGroup = (groupId: string) => {
    if (!selectedScreen || !selectedShowtime) return;

    const updatedScreens = screens.map((screen) =>
      screen.id === selectedScreen.id
        ? {
            ...screen,
            showtimes: screen.showtimes.map((showtime) =>
              showtime.id === selectedShowtime.id
                ? {
                    ...showtime,
                    seatGroups: showtime.seatGroups.filter(
                      (group) => group.id !== groupId
                    ),
                  }
                : showtime
            ),
          }
        : screen
    );

    api.delete(`/org/events/${event.id}/seat-groups/${groupId}`).then(() => {
      setScreens(updatedScreens);
      setSelectedScreen(
        updatedScreens.find((s) => s.id === selectedScreen.id)!
      );
      setSelectedShowtime((prev) =>
        prev
          ? {
              ...prev,
              seatGroups: prev.seatGroups.filter(
                (group) => group.id !== groupId
              ),
            }
          : null
      );
    });
  };

  const lastSelectedIndexRef = useRef<number | null>(null);

  const toggleSeatSelection = (seatId: string, e: React.MouseEvent) => {
    if (!selectedShowtime) return;

    const seats = selectedShowtime.seats;
    const seatIndex = seats.findIndex((s) => s.id === seatId);
    const seat = seats[seatIndex];

    if (
      !seat ||
      seat.state === 'booked' ||
      seat.state === 'disabled' ||
      seat.type === 'space'
    ) {
      return;
    }

    if (e.shiftKey && lastSelectedIndexRef.current !== null) {
      // --- Shift selection logic ---
      const start = Math.min(lastSelectedIndexRef.current, seatIndex);
      const end = Math.max(lastSelectedIndexRef.current, seatIndex);

      const rangeSeats = seats
        .slice(start, end + 1)
        .filter(
          (s) =>
            s.state !== 'booked' && s.state !== 'disabled' && s.type !== 'space'
        )
        .map((s) => s.id);

      const isSelecting = !selectedSeats.includes(seatId); // true = selecting, false = deselecting

      setSelectedSeats((prev) => {
        if (isSelecting) {
          return Array.from(new Set([...prev, ...rangeSeats]));
        } else {
          return prev.filter((id) => !rangeSeats.includes(id));
        }
      });
    } else {
      // --- Normal toggle ---
      setSelectedSeats((prev) =>
        prev.includes(seatId)
          ? prev.filter((id) => id !== seatId)
          : [...prev, seatId]
      );
    }

    lastSelectedIndexRef.current = seatIndex;
  };

  const getSeatColor = (seat: Seat) => {
    if (!selectedShowtime) return 'bg-gray-300';

    if (!selectedShowtime) return '#d1d5db'; // bg-gray-300

    if (['booked', 'booking'].includes(seat.state)) {
      return '#ef4444'; // bg-red-500
    }

    if (seat.state === 'disabled') {
      return '#6b7280'; // bg-gray-500
    }

    if (seat.type === 'space') {
      return 'transparent'; // bg-transparent
    }

    if (selectedSeats.includes(seat.id)) {
      return '#fbbf24';
    }

    const seatGroup = selectedShowtime.seatGroups.find((group) =>
      group.seats.includes(seat.id)
    );
    return seatGroup ? seatGroup.color : '#d1d5db';
  };

  const getUniqueRows = () => {
    if (!selectedShowtime) return [];
    /** @ts-ignore */
    const rows = [...new Set(selectedShowtime.seats.map((seat) => seat.row))];
    return rows.sort();
  };

  const getSeatsInRow = (row: string) => {
    if (!selectedShowtime) return [];
    return selectedShowtime.seats.filter((seat) => seat.row === row);
  };

  const getLastSeatsInRow = (row: string): number => {
    if (!selectedShowtime) return 1;
    const seatsInRow = selectedShowtime.seats.filter(
      (seat) => seat.row === row && seat.type === 'seat'
    );
    return seatsInRow.length > 0
      ? parseInt(seatsInRow[seatsInRow.length - 1].label, 10) + 1
      : 1;
  };

  const closeModalStudio = () => {
    setShowScreenForm({
      ...showScreenForm,
      state: false,
    });
  };

  return (
    <>
      <Modal
        show={showDeleteScreenModal}
        onRequestClose={() => setShowDeleteScreenModal(false)}
        width='medium'
        title='Hapus Studio'
        body={
          <>
            <p>Apakah kamu yakin ingin menghapus studio ini?</p>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setShowDeleteScreenModal(false)}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              variant={'danger'}
              onClick={() => {
                deleteStudio(selectedScreen?.id);
              }}
              leftIcon={<Trash2 />}
            >
              Ya, Hapus
            </Button>
          </div>
        }
      />

      <Modal
        show={showScreenForm.state}
        onRequestClose={closeModalStudio}
        title={
          showScreenForm.label === 'edit' ? 'Edit studio' : 'Tambah studio'
        }
        body={
          <>
            <div className='grid grid-cols-2 gap-3'>
              <div className='mb-3'>
                <FormLabel label='Nama studio' errorText={errors.name} required>
                  <Input
                    type='text'
                    value={showScreenForm.name}
                    onChange={(e) =>
                      setShowScreenForm({
                        ...showScreenForm,
                        name: e.target.value,
                      })
                    }
                    required
                  />
                </FormLabel>
              </div>
              <div className='mb-3'>
                <FormLabel
                  label='Tanggal tayang'
                  errorText={errors.date}
                  required
                >
                  <Input
                    type='date'
                    value={showScreenForm.date}
                    onChange={(e) =>
                      setShowScreenForm({
                        ...showScreenForm,
                        date: e.target.value,
                      })
                    }
                    required
                  />
                </FormLabel>
              </div>
            </div>

            <div className='mb-3'>
              <FormLabel
                label='Waktu mulai dijual'
                errorText={errors.start_sell_at}
                required
              >
                <Input
                  type='datetime-local'
                  value={moment(showScreenForm.start_sell_at).format(
                    'YYYY-MM-DDTHH:mm'
                  )}
                  onChange={(e) =>
                    setShowScreenForm({
                      ...showScreenForm,
                      start_sell_at: e.target.value,
                    })
                  }
                  required
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel
                label='Waktu berakhir dijual'
                errorText={errors.end_sell_at}
                required
              >
                <Input
                  type='datetime-local'
                  value={moment(showScreenForm.end_sell_at).format(
                    'YYYY-MM-DDTHH:mm'
                  )}
                  onChange={(e) =>
                    setShowScreenForm({
                      ...showScreenForm,
                      end_sell_at: e.target.value,
                    })
                  }
                  required
                />
              </FormLabel>
            </div>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={closeModalStudio}
              leftIcon={<XMarkIcon />}
            >
              Tutup
            </Button>
            <Button
              onClick={() => {
                if (showScreenForm.label === 'create') {
                  createNewScreen();
                } else {
                  updateScreen(selectedScreen?.id);
                }
              }}
              leftIcon={<DocumentCheckIcon />}
            >
              Simpan
            </Button>
          </div>
        }
      />

      <div>
        {/* Screen Selection */}
        <div className='my-6'>
          <div className='mb-4 flex items-center justify-between'>
            <p className='text-base font-medium text-danger'>Studio</p>
            <div className={'flex justify-end gap-2'}>
              <Button
                variant={'primary'}
                onClick={() =>
                  setShowScreenForm({
                    ...showScreenForm,
                    label: 'create',
                    state: true,
                  })
                }
                leftIcon={<PlusIcon />}
              >
                Tambah Studio
              </Button>
              {selectedScreen && (
                <Button
                  variant={'neutral'}
                  onClick={() =>
                    setShowScreenForm({
                      ...showScreenForm,
                      ...selectedScreen,
                      label: 'edit',
                      state: true,
                    })
                  }
                  leftIcon={<Settings className='h-4 w-4' />}
                >
                  Edit Studio
                </Button>
              )}

              {selectedScreen && (
                <Button
                  variant={'danger'}
                  onClick={() => setShowDeleteScreenModal(true)}
                  leftIcon={<Trash2 className='h-4 w-4' />}
                />
              )}
            </div>
          </div>
          <div className='flex flex-wrap gap-2'>
            {screens.map((screen) => (
              <Button
                variant={
                  selectedScreen?.id === screen.id ? 'primary' : 'neutral'
                }
                leftIcon={<Monitor className='h-4 w-4' />}
                key={screen.id}
                onClick={() => {
                  setSelectedScreen(screen);
                  if (screen?.showtimes?.length > 0) {
                    setSelectedShowtime(screen.showtimes[0] || null);
                  }
                  setIsEditMode(false);
                }}
              >
                {screen.name}
              </Button>
            ))}
          </div>
          {/* Screen Info */}
          {selectedScreen && (
            <div className='my-6'>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-4'>
                <div>
                  <label className='block text-sm font-medium'>
                    Nama Studio
                  </label>
                  <div className='mt-1 text-lg font-semibold'>
                    {selectedScreen.name}
                  </div>
                </div>
                <div>
                  <label className='block text-sm font-medium'>Tanggal</label>
                  <div className='mt-1'>
                    {moment(selectedScreen.date).format('lll')}
                  </div>
                </div>
                <div>
                  <label className='block text-sm font-medium'>
                    Mulai Jual
                  </label>
                  <div className='mt-1'>
                    {moment(selectedScreen.start_sell_at).format('lll')}
                  </div>
                </div>
                <div>
                  <label className='block text-sm font-medium'>
                    Selesai Jual
                  </label>
                  <div className='mt-1'>
                    {moment(selectedScreen.end_sell_at).format('lll')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <HSeparator />
        {selectedScreen && (
          <div className='my-6'>
            {/* Control Panel */}
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              {/* Showtime Selection and Management */}
              <div>
                <div className='mb-3 flex items-center justify-between'>
                  <p className='text-base font-medium text-danger'>
                    Jam Tayang
                  </p>
                </div>

                {/* Add New Showtime */}
                <div className='space-y-2 rounded'>
                  <div className='flex gap-2'>
                    <FormLabel label='Jam tayang'>
                      <div className={'flex flex-row gap-2'}>
                        <Input
                          type='time'
                          value={newShowtimeTime}
                          onChange={(e) => setNewShowtimeTime(e.target.value)}
                        />
                        <Button
                          onClick={createNewShowtime}
                          disabled={!newShowtimeTime}
                          className='w-full'
                        >
                          Tambah jam tayang
                        </Button>
                      </div>
                    </FormLabel>
                  </div>
                </div>

                {/* Existing Showtimes */}
                {selectedScreen?.showtimes?.length > 0 && (
                  <div className='mt-2 grid grid-cols-1 gap-2'>
                    {selectedScreen?.showtimes.map((showtime) => (
                      <div className='flex flex-row gap-2'>
                        <Button
                          variant={
                            selectedShowtime?.id === showtime.id
                              ? 'primary'
                              : 'neutral'
                          }
                          key={showtime.id}
                          onClick={() => {
                            setSelectedShowtime(showtime);
                            setSelectedSeats([]);
                          }}
                        >
                          <div>
                            <span>{showtime.time}</span>
                            <span className='ml-2 text-xs'>
                              {showtime.seats.length} seats
                            </span>
                          </div>
                        </Button>
                        {/* {selectedShowtime?.id === showtime.id && (
                          <Button
                            variant={'primary'}
                            key={showtime.id}
                            onClick={() => {
                              setSelectedShowtime(showtime);
                              setSelectedSeats([]);
                            }}
                            rightIcon={<PencilIcon />}
                          />
                        )} */}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Seat Groups Management */}
              {selectedShowtime && (
                <div>
                  <p className='text-base font-medium text-danger'>
                    Seat Groups
                  </p>

                  {/* Create New Seat Group */}
                  {isEditMode && (
                    <div className='my-4 space-y-3'>
                      <FormLabel label='Nama grup'>
                        <Input
                          type='text'
                          placeholder='e.g., VIP'
                          value={newSeatGroupName}
                          onChange={(e) => setNewSeatGroupName(e.target.value)}
                        />
                      </FormLabel>
                      <FormLabel label='Harga seat'>
                        <Input
                          type='price'
                          placeholder='100000'
                          value={newSeatGroupPrice || ''}
                          onChange={(e) =>
                            setNewSeatGroupPrice(parseInt(e.target.value) || 0)
                          }
                        />
                      </FormLabel>
                      <Button
                        onClick={createSeatGroup}
                        disabled={
                          !newSeatGroupName || selectedSeats.length === 0
                        }
                        className='w-full'
                      >
                        Create Group ({selectedSeats.length} seats)
                      </Button>
                    </div>
                  )}

                  {/* Existing Seat Groups */}
                  <div className='space-y-2'>
                    {selectedShowtime.seatGroups.map((group) => (
                      <div key={group.id} className='rounded border p-3'>
                        {editingSeatGroup?.id === group.id ? (
                          <div className='space-y-2'>
                            <FormLabel label='Nama grup'>
                              <Input
                                type='text'
                                value={editingSeatGroup.name}
                                onChange={(e) =>
                                  setEditingSeatGroup({
                                    ...editingSeatGroup,
                                    name: e.target.value,
                                  })
                                }
                              />
                            </FormLabel>
                            <FormLabel label='Harga'>
                              <Input
                                type='price'
                                value={editingSeatGroup.price}
                                onChange={(e) =>
                                  setEditingSeatGroup({
                                    ...editingSeatGroup,
                                    price: parseInt(e.target.value) || 0,
                                  })
                                }
                              />
                            </FormLabel>
                            <div className='flex gap-2'>
                              <Button
                                variant={'primary'}
                                onClick={() => {
                                  updateSeatGroup(group.id, {
                                    name: editingSeatGroup.name,
                                    price: editingSeatGroup.price,
                                  });
                                  setEditingSeatGroup(null);
                                }}
                              >
                                <Save className='h-4 w-4' />
                              </Button>
                              <Button
                                variant={'danger'}
                                onClick={() => setEditingSeatGroup(null)}
                              >
                                <X className='h-4 w-4' />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className='flex items-center justify-between'>
                            <div>
                              <div className='flex items-center gap-2'>
                                <div
                                  className={`h-4 w-4 rounded`}
                                  style={{
                                    backgroundColor: group.color,
                                  }}
                                />
                                <span className='font-medium'>
                                  {group.name}
                                </span>
                              </div>
                              <div className='text-sm text-gray-500'>
                                Rp {group.price.toLocaleString()} •{' '}
                                {group.seats.length} seats
                              </div>
                            </div>
                            <div className='flex gap-1'>
                              <Button
                                onClick={() => setEditingSeatGroup(group)}
                              >
                                <Edit2 className='h-3 w-3' />
                              </Button>
                              <Button
                                variant={'danger'}
                                onClick={() => deleteSeatGroup(group.id)}
                              >
                                <Trash2 className='h-3 w-3' />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <HSeparator />

        {selectedScreen && (
          <div>
            {/* Seat Layout */}
            <div className='my-6'>
              <div>
                <div className='mb-4 flex items-center justify-between'>
                  <p className='text-base font-medium text-danger'>
                    {selectedScreen.name} Layout
                  </p>
                  {selectedShowtime && (
                    <Button
                      variant={`${isEditMode ? 'neutral' : 'primary'}`}
                      onClick={() => setIsEditMode(!isEditMode)}
                      leftIcon={
                        isEditMode ? (
                          <X className='h-4 w-4' />
                        ) : (
                          <Edit2 className='h-4 w-4' />
                        )
                      }
                    >
                      {isEditMode ? 'Simpan Layout' : 'Edit Layout'}
                    </Button>
                  )}
                </div>

                {/* Screen */}
                {selectedShowtime && (
                  <div className='mb-12 rounded border border-dashed border-gray-800 bg-gray-100 py-3 text-center text-black'>
                    Layar/Panggung
                  </div>
                )}

                <div className={'flex justify-center'}>
                  {/* Seats */}
                  {getUniqueRows().length > 0 && (
                    <div className='space-y-4'>
                      {getUniqueRows().map((row) => (
                        <div key={row} className='flex items-center gap-2'>
                          <div className='w-8 text-center font-semibold'>
                            {row}
                          </div>
                          <div className='flex flex-wrap gap-1'>
                            {getSeatsInRow(row).map((seat) => (
                              <div key={seat.id} className='group relative'>
                                <button
                                  onClick={(e: any) => {
                                    if (isEditMode) {
                                      if (seat.state !== 'booked') {
                                        toggleSeatSelection(seat.id, e);
                                      }
                                    } else {
                                      setIsEditMode(true);
                                      toggleSeatSelection(seat.id, e);
                                    }
                                  }}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    if (isEditMode && seat.state !== 'booked') {
                                      const currentState = seat.state;
                                      const nextState =
                                        currentState === 'available'
                                          ? 'disabled'
                                          : 'available';
                                      updateSeatState(seat.id, nextState);
                                    }
                                  }}
                                  style={{
                                    backgroundColor: getSeatColor(seat),
                                  }}
                                  className={`h-8 w-8 rounded text-xs font-medium ${
                                    seat.type === 'space'
                                      ? 'text-gray-400'
                                      : 'text-white'
                                  }} 
                                ${seat.state === 'booked' ? 'cursor-not-allowed' : 'hover:opacity-80'}
                                ${seat.state === 'disabled' ? 'opacity-50' : ''}`}
                                  disabled={seat.state === 'booked'}
                                  title={`${seat.type === 'space' ? 'Space' : 'Seat'} ${seat.row}${seat.label} - ${seat.state} ${isEditMode ? '(Right-click to toggle state)' : ''}`}
                                >
                                  {typeof seat.id === 'string' ? (
                                    <SpinnerIcon className={'spinner ml-2'} />
                                  ) : seat.type === 'space' ? (
                                    ''
                                  ) : (
                                    seat.row + seat.label
                                  )}
                                </button>
                                {typeof seat.id !== 'string' &&
                                  isEditMode &&
                                  !['booked', 'booking'].includes(
                                    seat.state
                                  ) && (
                                    <button
                                      onClick={() =>
                                        removeSeatFromLayout(seat.id)
                                      }
                                      className='absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white hover:bg-red-600'
                                    >
                                      ×
                                    </button>
                                  )}
                              </div>
                            ))}
                            {isEditMode && (
                              <div className='flex flex-col gap-1'>
                                <select
                                  value={addSeatType}
                                  onChange={(e) =>
                                    setAddSeatType(
                                      e.target.value as 'seat' | 'space'
                                    )
                                  }
                                  className='w-16 rounded border px-1 text-xs'
                                >
                                  <option value='seat'>Seat</option>
                                  <option value='space'>Area Kosong</option>
                                </select>
                                <button
                                  onClick={() =>
                                    addSeatToLayout(
                                      row,
                                      addSeatType === 'space'
                                        ? 0
                                        : getLastSeatsInRow(row),
                                      addSeatType
                                    )
                                  }
                                  className='flex h-8 w-8 items-center justify-center rounded border-2 border-dashed border-gray-300 text-xs hover:border-blue-500'
                                  title={`Add ${addSeatType}`}
                                >
                                  <Plus className='h-3 w-3 text-gray-400' />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {isEditMode && (
                        <div className='mt-4 flex items-center gap-2'>
                          <input
                            type='text'
                            placeholder='Row (e.g., C)'
                            className='w-16 rounded border px-2 py-1'
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const value = (
                                  e.target as HTMLInputElement
                                ).value.toUpperCase();
                                if (value && !getUniqueRows().includes(value)) {
                                  addSeatToLayout(value, 1);
                                  (e.target as HTMLInputElement).value = '';
                                }
                              }
                            }}
                          />
                          <span className='text-sm text-gray-500'>
                            Press Enter to add new row
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Legend */}
                {selectedShowtime && (
                  <div className='mt-6 border-t pt-4'>
                    <div className='flex flex-wrap gap-4 text-sm'>
                      {selectedShowtime.seatGroups.map((group) => (
                        <div key={group.id} className='flex items-center gap-2'>
                          <div
                            className={`h-4 w-4 rounded`}
                            style={{
                              backgroundColor: group.color,
                            }}
                          />
                          <span>
                            {group.name} - Rp {group.price.toLocaleString()}
                          </span>
                        </div>
                      ))}
                      <div className='flex items-center gap-2'>
                        <div className='h-4 w-4 rounded bg-red-500'></div>
                        <span>Dipesan</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='h-4 w-4 rounded bg-gray-500 opacity-50'></div>
                        <span>Tidak Tersedia</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='h-4 w-4 rounded bg-yellow-400'></div>
                        <span>Dipilih</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='h-4 w-4 rounded border-2 border-dashed border-gray-300'></div>
                        <span>Area Kosong</span>
                      </div>
                      {pendingUpdates.length > 0 && (
                        <div className='flex items-center gap-2 text-orange-600'>
                          <div className='h-2 w-2 animate-pulse rounded-full bg-orange-500'></div>
                          <span>
                            memuat {pendingUpdates.length} perubahan...
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SeatingBookingSystem;
